// src/components/PhantomMobileTest.tsx - Test Component for Mobile Transaction Debugging
import React, { useState, useEffect } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { Transaction, SystemProgram, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import phantomDeeplinkService from '../services/phantom-deeplink.service';
import solanaService from '../services/solana.service';
import bs58 from 'bs58';

const PhantomMobileTest: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const [testResults, setTestResults] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<Record<string, any>>({});

  const addResult = (message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const formatted = `[${timestamp}] [${type.toUpperCase()}] ${message}`;
    setTestResults(prev => [...prev, formatted]);
    console.log(formatted);
  };

  const clearResults = () => {
    setTestResults([]);
    setDebugInfo({});
  };

  // Test 1: Check Session Status
  const testSessionStatus = async () => {
    addResult('=== Testing Session Status ===');
    
    const hasValidSession = phantomDeeplinkService.hasValidSession();
    const storedPublicKey = phantomDeeplinkService.getStoredPublicKey();
    const session = localStorage.getItem('phantom_session');
    const sharedSecret = localStorage.getItem('phantom_shared_secret');
    const encryptionKey = localStorage.getItem('phantom_encryption_public_key');
    const dappKey = localStorage.getItem('phantom_dapp_public_key');
    
    addResult(`Has valid session: ${hasValidSession}`, hasValidSession ? 'success' : 'error');
    addResult(`Stored public key: ${storedPublicKey || 'NONE'}`, storedPublicKey ? 'success' : 'error');
    addResult(`Session token: ${session ? session.substring(0, 20) + '...' : 'NONE'}`, session ? 'success' : 'error');
    addResult(`Shared secret: ${sharedSecret ? 'EXISTS' : 'NONE'}`, sharedSecret ? 'success' : 'error');
    addResult(`Phantom encryption key: ${encryptionKey ? encryptionKey.substring(0, 20) + '...' : 'NONE'}`, encryptionKey ? 'success' : 'error');
    addResult(`Dapp key: ${dappKey ? dappKey.substring(0, 20) + '...' : 'NONE'}`, dappKey ? 'success' : 'error');
    
    setDebugInfo(prev => ({
      ...prev,
      session: {
        hasValidSession,
        storedPublicKey,
        hasSession: !!session,
        hasSharedSecret: !!sharedSecret,
        hasEncryptionKey: !!encryptionKey,
        hasDappKey: !!dappKey
      }
    }));
  };

  // Test 2: Test Mobile Detection
  const testMobileDetection = () => {
    addResult('=== Testing Mobile Detection ===');
    
    const userAgent = navigator.userAgent;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
    const isInPhantomBrowser = !!(window as any).solana?.isPhantom && isMobile;
    const hasPhantomProvider = !!(window as any).solana;
    
    addResult(`User Agent: ${userAgent}`);
    addResult(`Is Mobile: ${isMobile}`, isMobile ? 'success' : 'info');
    addResult(`Is in Phantom Browser: ${isInPhantomBrowser}`, isInPhantomBrowser ? 'success' : 'info');
    addResult(`Has Phantom Provider: ${hasPhantomProvider}`, hasPhantomProvider ? 'success' : 'warning');
    
    setDebugInfo(prev => ({
      ...prev,
      device: {
        isMobile,
        isInPhantomBrowser,
        hasPhantomProvider,
        userAgent: userAgent.substring(0, 100)
      }
    }));
  };

  // Test 3: Create Test Transaction
  const createTestTransaction = async (): Promise<Transaction | null> => {
    try {
      if (!publicKey) {
        addResult('No wallet connected', 'error');
        return null;
      }

      addResult('Creating test transaction...');
      
      const fromPubkey = new PublicKey(publicKey);
      const toPubkey = new PublicKey('11111111111111111111111111111112'); // System Program
      
      const transaction = new Transaction();
      transaction.add(
        SystemProgram.transfer({
          fromPubkey,
          toPubkey,
          lamports: 1000000, // 0.001 SOL
        })
      );
      
      // Get recent blockhash
      const { blockhash } = await solanaService['connection'].getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromPubkey;
      
      addResult('Test transaction created successfully', 'success');
      addResult(`Transaction size: ${transaction.serialize({ requireAllSignatures: false }).length} bytes`);
      
      return transaction;
    } catch (error: any) {
      addResult(`Failed to create test transaction: ${error.message}`, 'error');
      return null;
    }
  };

  // Test 4: Test Transaction Serialization
  const testTransactionSerialization = async () => {
    addResult('=== Testing Transaction Serialization ===');
    
    const transaction = await createTestTransaction();
    if (!transaction) return;
    
    try {
      // Test serialization with requireAllSignatures: false
      const serialized = transaction.serialize({ requireAllSignatures: false });
      const base58Encoded = bs58.encode(serialized);
      
      addResult(`Serialized length: ${serialized.length} bytes`, 'success');
      addResult(`Base58 encoded length: ${base58Encoded.length} chars`, 'success');
      addResult(`Base58 preview: ${base58Encoded.substring(0, 50)}...`);
      
      setDebugInfo(prev => ({
        ...prev,
        transaction: {
          serializedLength: serialized.length,
          base58Length: base58Encoded.length,
          base58Preview: base58Encoded.substring(0, 100)
        }
      }));
      
    } catch (error: any) {
      addResult(`Serialization failed: ${error.message}`, 'error');
    }
  };

  // Test 5: Test Encryption
  const testEncryption = async () => {
    addResult('=== Testing Encryption ===');
    
    const session = localStorage.getItem('phantom_session');
    const sharedSecret = localStorage.getItem('phantom_shared_secret');
    
    if (!session) {
      addResult('No session token available', 'error');
      return;
    }
    
    if (!sharedSecret) {
      addResult('No shared secret available', 'error');
      return;
    }
    
    try {
      const testPayload = {
        transaction: 'AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAEDnDnm6iF9XYk0',
        session: session
      };
      
      addResult(`Test payload: ${JSON.stringify(testPayload).substring(0, 100)}...`);
      
      // Test the encryption that will be used
      const testMessage = JSON.stringify(testPayload);
      const messageBytes = new TextEncoder().encode(testMessage);
      
      addResult(`Message bytes length: ${messageBytes.length}`, 'success');
      addResult(`Session token length: ${session.length}`, 'success');
      
      setDebugInfo(prev => ({
        ...prev,
        encryption: {
          payloadSize: testMessage.length,
          sessionLength: session.length,
          hasSharedSecret: !!sharedSecret
        }
      }));
      
    } catch (error: any) {
      addResult(`Encryption test failed: ${error.message}`, 'error');
    }
  };

  // Test 6: Test Complete Mobile Transaction Flow (Dry Run)
  const testMobileTransactionFlow = async () => {
    addResult('=== Testing Mobile Transaction Flow (Dry Run) ===');
    setIsLoading(true);
    
    try {
      const transaction = await createTestTransaction();
      if (!transaction) {
        setIsLoading(false);
        return;
      }
      
      // Check prerequisites
      const hasValidSession = phantomDeeplinkService.hasValidSession();
      const session = localStorage.getItem('phantom_session');
      const dappKey = localStorage.getItem('phantom_dapp_public_key');
      
      if (!hasValidSession) {
        addResult('Invalid session - would fail', 'error');
        setIsLoading(false);
        return;
      }
      
      if (!session) {
        addResult('No session token - would fail', 'error');
        setIsLoading(false);
        return;
      }
      
      if (!dappKey) {
        addResult('No dapp encryption key - would fail', 'error');
        setIsLoading(false);
        return;
      }
      
      // Serialize transaction
      const serializedTransaction = bs58.encode(
        transaction.serialize({ requireAllSignatures: false })
      );
      
      addResult('Transaction serialized successfully', 'success');
      
      // Create payload (don't actually encrypt for dry run)
      const payload = {
        transaction: serializedTransaction,
        session: session
      };
      
      addResult(`Payload created: ${JSON.stringify(payload).length} chars`, 'success');
      
      // Build URL (don't actually redirect)
      const params = new URLSearchParams({
        dapp_encryption_public_key: dappKey,
        nonce: 'TEST_NONCE_123',
        redirect_link: `${window.location.origin}/phantom-signing-redirect`,
        payload: 'TEST_ENCRYPTED_PAYLOAD',
      });
      
      const phantomUrl = `https://phantom.app/ul/v1/signTransaction?${params.toString()}`;
      
      addResult('Deeplink URL constructed successfully', 'success');
      addResult(`URL length: ${phantomUrl.length} chars`);
      addResult(`URL preview: ${phantomUrl.substring(0, 200)}...`);
      
      addResult('DRY RUN COMPLETE - Would redirect to Phantom now', 'success');
      
    } catch (error: any) {
      addResult(`Mobile transaction flow test failed: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Test 7: Actual Mobile Transaction (LIVE)
  const testActualMobileTransaction = async () => {
    addResult('=== LIVE TEST: Actual Mobile Transaction ===');
    addResult('WARNING: This will attempt a real transaction!', 'warning');
    
    if (!window.confirm('This will attempt a real 0.001 SOL transaction. Continue?')) {
      addResult('Test cancelled by user', 'info');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const transaction = await createTestTransaction();
      if (!transaction) {
        setIsLoading(false);
        return;
      }
      
      addResult('Starting LIVE mobile transaction...', 'warning');
      
      // This should trigger the mobile signing flow
      const result = await solanaService.signTransaction(transaction);
      
      addResult(`Transaction completed: ${result}`, 'success');
      
    } catch (error: any) {
      addResult(`LIVE transaction failed: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Run all tests
  const runAllTests = async () => {
    clearResults();
    setIsLoading(true);
    
    try {
      await testSessionStatus();
      await testMobileDetection();
      await testTransactionSerialization();
      await testEncryption();
      await testMobileTransactionFlow();
      
      addResult('=== ALL TESTS COMPLETED ===', 'success');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Auto-run basic tests when component mounts
    if (isConnected) {
      setTimeout(() => {
        testSessionStatus();
        testMobileDetection();
      }, 1000);
    }
  }, [isConnected]);

  if (!isConnected) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700 text-center">
        <h2 className="text-2xl font-bold mb-4">Phantom Mobile Test</h2>
        <p className="text-gray-400">Please connect your wallet first</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
      <h2 className="text-2xl font-bold mb-6">Phantom Mobile Transaction Test</h2>
      
      {/* Test Controls */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <button
          onClick={testSessionStatus}
          disabled={isLoading}
          className="px-3 py-2 bg-blue-600/20 text-blue-400 rounded hover:bg-blue-600/30 transition-all text-sm disabled:opacity-50"
        >
          Session Status
        </button>
        <button
          onClick={testMobileDetection}
          disabled={isLoading}
          className="px-3 py-2 bg-green-600/20 text-green-400 rounded hover:bg-green-600/30 transition-all text-sm disabled:opacity-50"
        >
          Device Detection
        </button>
        <button
          onClick={testTransactionSerialization}
          disabled={isLoading}
          className="px-3 py-2 bg-yellow-600/20 text-yellow-400 rounded hover:bg-yellow-600/30 transition-all text-sm disabled:opacity-50"
        >
          Transaction
        </button>
        <button
          onClick={testEncryption}
          disabled={isLoading}
          className="px-3 py-2 bg-purple-600/20 text-purple-400 rounded hover:bg-purple-600/30 transition-all text-sm disabled:opacity-50"
        >
          Encryption
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <button
          onClick={testMobileTransactionFlow}
          disabled={isLoading}
          className="px-4 py-2 bg-orange-600/20 text-orange-400 rounded hover:bg-orange-600/30 transition-all disabled:opacity-50"
        >
          Dry Run Test
        </button>
        <button
          onClick={testActualMobileTransaction}
          disabled={isLoading}
          className="px-4 py-2 bg-red-600/20 text-red-400 rounded hover:bg-red-600/30 transition-all disabled:opacity-50"
        >
          LIVE Test (0.001 SOL)
        </button>
        <button
          onClick={runAllTests}
          disabled={isLoading}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          Run All Tests
        </button>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Test Results</h3>
        <button
          onClick={clearResults}
          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors"
        >
          Clear
        </button>
      </div>

      {/* Results Display */}
      <div className="bg-black/50 rounded-lg p-4 mb-6 h-96 overflow-y-auto">
        <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
          {testResults.length > 0 ? testResults.join('\n') : 'No test results yet. Click a test button above.'}
        </pre>
      </div>

      {/* Debug Info */}
      {Object.keys(debugInfo).length > 0 && (
        <div>
          <h4 className="font-semibold mb-2">Debug Information</h4>
          <div className="bg-gray-900/50 rounded-lg p-4">
            <pre className="text-xs text-gray-400 whitespace-pre-wrap">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 p-6 rounded-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-400 mx-auto mb-4"></div>
            <p className="text-white">Running test...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhantomMobileTest;